import React from 'react';
import { motion } from 'framer-motion';
import {
  ExternalLink,
  Heart,
  FileText,
  Calculator,
  PiggyBank,
} from 'lucide-react';
import {
  fadeInUp,
  staggerContainer,
  fastStaggerContainer,
  scaleIn,
} from '../utils/animations';

export default function Donations() {
  return (
    <div className="relative min-h-screen bg-gradient-to-b from-gray-50 via-blue-50/30 to-gray-50 py-16 overflow-hidden">
      {/* Subtle Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-64 h-64 bg-blue-400/8 rounded-full blur-3xl"
          animate={{
            y: [-10, 10, -10],
            transition: {
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute bottom-40 right-20 w-80 h-80 bg-cyan-400/8 rounded-full blur-3xl"
          animate={{
            y: [10, -10, 10],
            transition: {
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 w-72 h-72 bg-purple-400/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
            transition: {
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />

        <div className="absolute inset-0 bg-[linear-gradient(rgba(2,73,148,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(2,73,148,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />

        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-blue-400/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -80, 0],
              opacity: [0, 0.6, 0],
              transition: {
                duration: 5 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 3,
                ease: 'easeInOut',
              },
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <motion.div
          className="text-center mb-16"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <motion.h1
            className="text-4xl font-bold text-gray-900 mb-6"
            variants={fadeInUp}
          >
            Redirecționează 3,5% din impozitul pe venit
          </motion.h1>
          <motion.p
            className="text-xl text-gray-600 max-w-3xl mx-auto mb-8"
            variants={fadeInUp}
          >
            Susține activitatea Asociației Medicale Multidisciplinare prin
            redirecționarea a 3,5% din impozitul pe venit, fără să te coste
            nimic!
          </motion.p>
          <motion.a
            href="https://redirectioneazaimpozit.ro/asociatia-medicala-multidisciplinara"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 bg-[#024994] text-white rounded-full text-lg font-semibold hover:bg-[#024994]/90 transition"
            variants={fadeInUp}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ExternalLink className="w-5 h-5 mr-2" />
            Completează formularul online
          </motion.a>
        </motion.div>

        {/* Main Content */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: '-50px' }}
          variants={fastStaggerContainer}
        >
          <motion.div
            className="bg-white rounded-xl shadow-lg p-8"
            variants={scaleIn}
            whileHover={{ y: -5, transition: { duration: 0.3 } }}
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Ce este redirecționarea a 3,5% din impozitul pe venit?
            </h2>
            <p className="text-gray-600 mb-4">
              Conform legislației fiscale din România (Legea nr. 227/2015
              privind Codul fiscal), orice contribuabil poate direcționa 3,5%
              din impozitul pe venit către o organizație non-profit sau unitate
              de cult.
            </p>
            <p className="text-gray-600 mb-4">
              Acest mecanism permite contribuabililor să decidă unde merge o
              parte din impozitul pe care oricum îl datorează statului, fără
              niciun cost suplimentar pentru ei.
            </p>
            <p className="text-gray-600">
              Practic, în loc să meargă la bugetul de stat, această sumă va fi
              direcționată către organizația aleasă de dumneavoastră.
            </p>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-lg p-8"
            variants={scaleIn}
            whileHover={{ y: -5, transition: { duration: 0.3 } }}
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              De ce să alegi Asociația Medicală Multidisciplinară?
            </h2>
            <p className="text-gray-600 mb-4">
              Fondurile strânse prin redirecționarea impozitului ne ajută să:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
              <li>Oferim consultații pacienților la costuri minime</li>
              <li>Dezvoltăm platforma pentru a oferi servicii mai bune</li>
              <li>Organizăm sesiuni de informare și educare pentru pacienți</li>
              <li>Susținem cercetarea în domeniul oncologic</li>
            </ul>
            <p className="text-gray-600">
              Fiecare contribuție ne ajută să facem serviciile medicale de
              calitate mai accesibile pentru toți pacienții.
            </p>
          </motion.div>
        </motion.div>

        {/* Steps Section */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-8 mb-16"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: '-100px' }}
          variants={staggerContainer}
        >
          <motion.h2
            className="text-2xl font-bold text-gray-900 mb-8 text-center"
            variants={fadeInUp}
          >
            Cum poți redirecționa 3,5% din impozitul pe venit?
          </motion.h2>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            variants={fastStaggerContainer}
          >
            <motion.div
              className="flex flex-col items-center text-center"
              variants={scaleIn}
            >
              <FileText className="w-12 h-12 text-[#024994] mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Completează formularul
              </h3>
              <p className="text-gray-600">
                Accesează link-ul de mai sus și completează formularul 230
                online. Procesul durează mai puțin de 5 minute.
              </p>
            </motion.div>
            <motion.div
              className="flex flex-col items-center text-center"
              variants={scaleIn}
            >
              <Calculator className="w-12 h-12 text-[#024994] mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Calculul se face automat
              </h3>
              <p className="text-gray-600">
                Nu trebuie să calculezi nimic - sistemul determină automat suma
                bazată pe veniturile tale declarate.
              </p>
            </motion.div>
            <motion.div
              className="flex flex-col items-center text-center"
              variants={scaleIn}
            >
              <Heart className="w-12 h-12 text-[#024994] mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Ai contribuit la o cauză nobilă
              </h3>
              <p className="text-gray-600">
                Felicitări! Ai ajutat la îmbunătățirea serviciilor medicale
                pentru pacienții oncologici.
              </p>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-8"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: '-100px' }}
          variants={scaleIn}
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Întrebări Frecvente
          </h2>
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Cine poate redirecționa 3,5% din impozit?
              </h3>
              <p className="text-gray-600">
                Orice persoană fizică care realizează venituri din salarii,
                pensii, activități independente, drepturi de proprietate
                intelectuală, chirii sau activități agricole.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Care este termenul limită?
              </h3>
              <p className="text-gray-600">
                Formularul 230 poate fi depus până la data de 25 mai a anului
                următor celui pentru care se face redirecționarea.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Trebuie să completez formularul în fiecare an?
              </h3>
              <p className="text-gray-600">
                Da, redirecționarea trebuie reînnoită anual prin completarea
                unui nou formular 230.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
